import { useEffect, useId, useMemo, useRef, useState } from "react";

import { formatChartAxisValue, shouldShowChartLabel, smoothLinePath } from "./chartUtils";
import "./ui.css";

interface AreaChartProps {
  values: number[];
  labels?: string[];
  height?: number;
  accent?: string;
  formatValue?: (v: number) => string;
}

export function AreaChart({
  values,
  labels,
  height = 200,
  accent,
  formatValue = formatChartAxisValue,
}: AreaChartProps) {
  const gradId = useId().replace(/[:]/g, "");
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(560);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setWidth(Math.round(w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const layout = useMemo(() => {
    const padL = 44;
    const padR = 12;
    const padT = 16;
    const padB = 28;
    const plotW = width - padL - padR;
    const plotH = height - padT - padB;
    const min = 0;
    const max = Math.max(...values, 1) * 1.12;
    const range = max - min || 1;
    const step = plotW / Math.max(values.length - 1, 1);

    const pts = values.map((v, i) => {
      const x = padL + i * step;
      const y = padT + plotH * (1 - (v - min) / range);
      return [x, y] as const;
    });

    const line = smoothLinePath(pts);
    const area =
      pts.length > 0
        ? `${line} L ${pts[pts.length - 1][0]} ${height - padB} L ${padL} ${height - padB} Z`
        : "";

    const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => ({
      v: max * t,
      y: padT + plotH * (1 - t),
    }));

    return { padL, padR, padT, padB, plotW, plotH, pts, line, area, ticks, step };
  }, [values, width, height]);

  if (values.length === 0) return null;

  const strokeColor = accent || "var(--accent)";
  const active =
    activeIndex != null && activeIndex >= 0 && activeIndex < layout.pts.length
      ? layout.pts[activeIndex]
      : null;
  const activeValue = activeIndex != null ? values[activeIndex] : null;
  const activeLabel = activeIndex != null ? labels?.[activeIndex] : null;

  const pickIndex = (clientX: number) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect?.width) return;
    const x = ((clientX - rect.left) / rect.width) * width;
    const raw = (x - layout.padL) / Math.max(layout.step, 1);
    const idx = Math.round(Math.max(0, Math.min(values.length - 1, raw)));
    setActiveIndex(idx);
  };

  return (
    <div className="area-chart-wrap" ref={wrapRef} onMouseLeave={() => setActiveIndex(null)}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="area-chart"
        width="100%"
        height={height}
        role="img"
        aria-label="Trend chart"
      >
        <defs>
          <linearGradient id={`grad-${gradId}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={strokeColor} stopOpacity="0.32" />
            <stop offset="55%" stopColor={strokeColor} stopOpacity="0.08" />
            <stop offset="100%" stopColor={strokeColor} stopOpacity="0" />
          </linearGradient>
        </defs>

        <rect
          x={layout.padL}
          y={layout.padT}
          width={layout.plotW}
          height={layout.plotH}
          rx={6}
          className="area-chart__plot-bg"
        />

        {layout.ticks.map((t, i) => (
          <g key={i}>
            <line
              x1={layout.padL}
              y1={t.y}
              x2={width - layout.padR}
              y2={t.y}
              className="area-chart__grid"
            />
            <text x={layout.padL - 8} y={t.y + 4} className="area-chart__y" textAnchor="end">
              {formatValue(t.v)}
            </text>
          </g>
        ))}

        {layout.area && <path d={layout.area} fill={`url(#grad-${gradId})`} />}
        {layout.line && (
          <path d={layout.line} className="area-chart__line" stroke={strokeColor} />
        )}

        {active && (
          <>
            <line
              x1={active[0]}
              y1={layout.padT}
              x2={active[0]}
              y2={height - layout.padB}
              className="area-chart__cursor"
            />
            <circle
              cx={active[0]}
              cy={active[1]}
              r="5"
              className="area-chart__dot area-chart__dot--active"
              stroke={strokeColor}
            />
          </>
        )}

        {labels?.map((l, i) =>
          shouldShowChartLabel(i, labels.length) ? (
            <text
              key={i}
              x={layout.pts[i]?.[0] ?? layout.padL}
              y={height - 8}
              className="area-chart__x"
              textAnchor="middle"
            >
              {l}
            </text>
          ) : null,
        )}

        <rect
          x={layout.padL}
          y={layout.padT}
          width={layout.plotW}
          height={layout.plotH}
          fill="transparent"
          className="area-chart__hit"
          onMouseMove={(e) => pickIndex(e.clientX)}
          onTouchMove={(e) => {
            const touch = e.touches[0];
            if (touch) pickIndex(touch.clientX);
          }}
        />
      </svg>

      {active && activeValue != null && (
        <div
          className="area-chart__tooltip"
          style={{
            left: `${(active[0] / width) * 100}%`,
            top: Math.max(0, active[1] - 12),
          }}
        >
          {activeLabel && <span className="area-chart__tooltip-label">{activeLabel}</span>}
          <span className="area-chart__tooltip-value">{formatValue(activeValue)}</span>
        </div>
      )}
    </div>
  );
}
